import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { 
  Users, Building2, Handshake, Mail, Phone, Calendar, Search, 
  Filter, Check, X, Eye, Edit, Trash2, Shield, Lock, LogIn,
  RefreshCw, Download, BarChart3, TrendingUp, UserPlus, Store,
  Clock, CheckCircle, XCircle, AlertCircle, Settings, LogOut,
  Database, Activity, History, Upload, FileJson, Archive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Status badges
const StatusBadge = ({ status }) => {
  const configs = {
    nuovo: { label: 'Nuovo', className: 'bg-blue-100 text-blue-700' },
    contattato: { label: 'Contattato', className: 'bg-yellow-100 text-yellow-700' },
    qualificato: { label: 'Qualificato', className: 'bg-purple-100 text-purple-700' },
    convertito: { label: 'Convertito', className: 'bg-green-100 text-green-700' },
    rifiutato: { label: 'Rifiutato', className: 'bg-red-100 text-red-700' },
    attivo: { label: 'Attivo', className: 'bg-green-100 text-green-700' },
    inattivo: { label: 'Inattivo', className: 'bg-gray-100 text-gray-700' },
    pending: { label: 'In Attesa', className: 'bg-orange-100 text-orange-700' }
  };
  const config = configs[status] || configs.nuovo;
  return <Badge className={config.className}>{config.label}</Badge>;
};

const ControlPanelPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  // Data
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({});
  const [tenants, setTenants] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modals
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  // Tenant Management
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantLogs, setTenantLogs] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantBackups, setTenantBackups] = useState([]);
  const [tenantStats, setTenantStats] = useState(null);
  const [loadingTenantData, setLoadingTenantData] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('controlpanel_token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp > Date.now() / 1000 && payload.role === 'controlpanel') {
        setIsAuthenticated(true);
        loadData(token);
      } else {
        localStorage.removeItem('controlpanel_token');
      }
    } catch {
      localStorage.removeItem('controlpanel_token');
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/controlpanel/login`, loginData);
      localStorage.setItem('controlpanel_token', response.data.access_token);
      setIsAuthenticated(true);
      loadData(response.data.access_token);
      toast.success('Accesso effettuato!');
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Credenziali non valide');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('controlpanel_token');
    setIsAuthenticated(false);
    setLeads([]);
    setPartners([]);
  };

  const loadData = async (token) => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [leadsRes, partnersRes, statsRes] = await Promise.all([
        axios.get(`${API}/controlpanel/leads`, { headers }),
        axios.get(`${API}/controlpanel/partners`, { headers }),
        axios.get(`${API}/controlpanel/stats`, { headers })
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setPartners(partnersRes.data.partners || []);
      setStats(statsRes.data || {});
      
      // Carica anche i tenants
      loadTenants();
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('controlpanel_token');
      await axios.put(`${API}/controlpanel/leads/${leadId}`, 
        { status: newStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Stato aggiornato!');
      loadData(token);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const updatePartnerStatus = async (partnerId, newStatus, monitoringAccess = null) => {
    try {
      const token = localStorage.getItem('controlpanel_token');
      const data = { status: newStatus };
      if (monitoringAccess !== null) {
        data.monitoring_access = monitoringAccess;
      }
      await axios.put(`${API}/controlpanel/partners/${partnerId}`, data,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Partner aggiornato!');
      loadData(token);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Eliminare questo contatto?')) return;
    try {
      const token = localStorage.getItem('controlpanel_token');
      await axios.delete(`${API}/controlpanel/leads/${leadId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Contatto eliminato');
      loadData(token);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // ============================================
  // TENANT MANAGEMENT FUNCTIONS
  // ============================================
  
  // Carica lista tenants
  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('controlpanel_token');
      const res = await axios.get(`${API}/controlpanel/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenants(res.data.tenants || []);
    } catch (error) {
      console.error('Errore caricamento tenants:', error);
      // Fallback: crea lista mock per test
      setTenants([
        { id: 'antonio_mittiga', nome: 'Antonio Mittiga', database_name: 'antonio_mittiga', status: 'attivo' }
      ]);
    }
  };
  
  // Seleziona un tenant e carica i suoi dati
  const selectTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setLoadingTenantData(true);
    setShowTenantModal(true);
    
    const token = localStorage.getItem('controlpanel_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const tenantId = tenant.database_name || tenant.id;
    
    try {
      // Carica dati in parallelo
      const [logsRes, usersRes, backupsRes, statsRes] = await Promise.allSettled([
        axios.get(`${API}/controlpanel/tenant-management/${tenantId}/logs?limit=50`, { headers }),
        axios.get(`${API}/controlpanel/tenant-management/${tenantId}/users`, { headers }),
        axios.get(`${API}/controlpanel/tenant-management/${tenantId}/backups`, { headers }),
        axios.get(`${API}/controlpanel/tenant-management/${tenantId}/stats`, { headers })
      ]);
      
      setTenantLogs(logsRes.status === 'fulfilled' ? logsRes.value.data.logs || [] : []);
      setTenantUsers(usersRes.status === 'fulfilled' ? usersRes.value.data.users || [] : []);
      setTenantBackups(backupsRes.status === 'fulfilled' ? backupsRes.value.data.backups || [] : []);
      setTenantStats(statsRes.status === 'fulfilled' ? statsRes.value.data.stats : null);
      
    } catch (error) {
      console.error('Errore caricamento dati tenant:', error);
      
    } finally {
      setLoadingTenantData(false);
    }
  };
  
  // Crea backup tenant
  const createTenantBackup = async () => {
    if (!selectedTenant) return;
    
    const token = localStorage.getItem('controlpanel_token');
    const tenantId = selectedTenant.database_name || selectedTenant.id;
    
    try {
      toast.info('Creazione backup in corso...');
      const res = await axios.post(`${API}/controlpanel/tenant-management/${tenantId}/backup`, 
        { type: 'manual', created_by: 'controlpanel' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Backup creato!');
      
      // Ricarica backups
      const backupsRes = await axios.get(`${API}/controlpanel/tenant-management/${tenantId}/backups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenantBackups(backupsRes.data.backups || []);
    } catch (error) {
      console.error('Errore creazione backup:', error);
      toast.error('Errore nella creazione del backup');
    }
  };
  
  // Ripristina backup tenant
  const restoreTenantBackup = async (backupId) => {
    if (!selectedTenant) return;
    if (!window.confirm('⚠️ ATTENZIONE: Questa operazione sovrascriverà tutti i dati attuali del tenant. Continuare?')) return;
    
    const token = localStorage.getItem('controlpanel_token');
    const tenantId = selectedTenant.database_name || selectedTenant.id;
    
    try {
      toast.info('Ripristino in corso...');
      const res = await axios.post(`${API}/controlpanel/tenant-management/${tenantId}/restore/${backupId}`, {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Ripristino completato!');
    } catch (error) {
      console.error('Errore ripristino:', error);
      toast.error('Errore nel ripristino del backup');
    }
  };
  
  // Download dati tenant
  const downloadTenantData = async () => {
    if (!selectedTenant) return;
    
    const token = localStorage.getItem('controlpanel_token');
    const tenantId = selectedTenant.database_name || selectedTenant.id;
    
    try {
      toast.info('Download in corso...');
      const res = await axios.get(`${API}/controlpanel/tenant-management/${tenantId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Crea file e scarica
      const dataStr = JSON.stringify(res.data.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenantId}_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Download completato!');
    } catch (error) {
      console.error('Errore download:', error);
      toast.error('Errore nel download dei dati');
    }
  };
  
  // Chiudi modal tenant
  const closeTenantModal = () => {
    setShowTenantModal(false);
    setSelectedTenant(null);
    setTenantLogs([]);
    setTenantUsers([]);
    setTenantBackups([]);
    setTenantStats(null);
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !searchTerm || 
      lead.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.ristorante?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || lead.tipo === filterType;
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Filter partners
  const filteredPartners = partners.filter(partner => {
    const matchesSearch = !searchTerm || 
      partner.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.azienda?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || partner.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Control Panel</CardTitle>
            <p className="text-gray-500 text-sm">Gestione Lead e Partner</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {loginError}
                </div>
              )}
              <div>
                <Label>Username</Label>
                <Input
                  value={loginData.username}
                  onChange={e => setLoginData({...loginData, username: e.target.value})}
                  placeholder="admin"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={e => setLoginData({...loginData, password: e.target.value})}
                  placeholder="••••••••"
                  className="mt-1"
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-blue-600">
                <LogIn className="h-4 w-4 mr-2" />
                Accedi
              </Button>
            </form>
            <p className="text-center text-xs text-gray-400 mt-6">
              Credenziali: ControlAdmin / Control2026!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Control Panel</h1>
              <p className="text-xs text-gray-500">Gestione Lead & Partner</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => loadData(localStorage.getItem('controlpanel_token'))}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Store className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_leads || 0}</p>
                  <p className="text-xs text-gray-500">Contatti Totali</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Handshake className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_partners || 0}</p>
                  <p className="text-xs text-gray-500">Partner</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.converted || 0}</p>
                  <p className="text-xs text-gray-500">Convertiti</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending || 0}</p>
                  <p className="text-xs text-gray-500">Da Contattare</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Cerca per nome, email, azienda..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="cliente">Ristoranti</SelectItem>
                  <SelectItem value="affiliato">Partner</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="nuovo">Nuovo</SelectItem>
                  <SelectItem value="contattato">Contattato</SelectItem>
                  <SelectItem value="qualificato">Qualificato</SelectItem>
                  <SelectItem value="convertito">Convertito</SelectItem>
                  <SelectItem value="rifiutato">Rifiutato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="space-y-4">
          <TabsList>
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              Contatti Ristoranti ({filteredLeads.filter(l => l.tipo === 'cliente').length})
            </TabsTrigger>
            <TabsTrigger value="partners" className="flex items-center gap-2">
              <Handshake className="h-4 w-4" />
              Richieste Partner ({filteredLeads.filter(l => l.tipo === 'affiliato').length})
            </TabsTrigger>
            <TabsTrigger value="active-partners" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Partner Attivi ({filteredPartners.length})
            </TabsTrigger>
            <TabsTrigger value="tenants" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Gestione Tenant ({tenants.length})
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab - Ristoranti */}
          <TabsContent value="leads">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Contatto</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Ristorante</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Contatti</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Data</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Stato</th>
                        <th className="text-right p-4 text-sm font-medium text-gray-600">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.filter(l => l.tipo === 'cliente').map(lead => (
                        <tr key={lead.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <div className="font-medium">{lead.nome} {lead.cognome}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm">{lead.ristorante || '-'}</div>
                            <div className="text-xs text-gray-500">{lead.citta}</div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1 text-sm">
                              <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>
                              <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.telefono}</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-gray-500">
                            {new Date(lead.created_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="p-4">
                            <Select value={lead.status || 'nuovo'} onValueChange={v => updateLeadStatus(lead.id, v)}>
                              <SelectTrigger className="w-[130px] h-8">
                                <StatusBadge status={lead.status || 'nuovo'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nuovo">Nuovo</SelectItem>
                                <SelectItem value="contattato">Contattato</SelectItem>
                                <SelectItem value="qualificato">Qualificato</SelectItem>
                                <SelectItem value="convertito">Convertito</SelectItem>
                                <SelectItem value="rifiutato">Rifiutato</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedLead(lead)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteLead(lead.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredLeads.filter(l => l.tipo === 'cliente').length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">
                            Nessun contatto ristorante trovato
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partner Requests Tab */}
          <TabsContent value="partners">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Richiedente</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Azienda</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Contatti</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Data</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Stato</th>
                        <th className="text-right p-4 text-sm font-medium text-gray-600">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.filter(l => l.tipo === 'affiliato').map(lead => (
                        <tr key={lead.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <div className="font-medium">{lead.nome} {lead.cognome}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm">{lead.azienda || '-'}</div>
                            <div className="text-xs text-gray-500">P.IVA: {lead.partita_iva || '-'}</div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1 text-sm">
                              <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>
                              <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.telefono}</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-gray-500">
                            {new Date(lead.created_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="p-4">
                            <Select value={lead.status || 'nuovo'} onValueChange={v => updateLeadStatus(lead.id, v)}>
                              <SelectTrigger className="w-[130px] h-8">
                                <StatusBadge status={lead.status || 'nuovo'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nuovo">Nuovo</SelectItem>
                                <SelectItem value="contattato">Contattato</SelectItem>
                                <SelectItem value="qualificato">Qualificato</SelectItem>
                                <SelectItem value="convertito">Convertito</SelectItem>
                                <SelectItem value="rifiutato">Rifiutato</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedLead(lead)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" className="text-green-600" onClick={() => {
                                // Convert to active partner
                                updateLeadStatus(lead.id, 'convertito');
                              }}>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Attiva
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteLead(lead.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredLeads.filter(l => l.tipo === 'affiliato').length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">
                            Nessuna richiesta partner trovata
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Active Partners Tab */}
          <TabsContent value="active-partners">
            <Card>
              <CardContent className="p-4">
                <div className="text-center py-8 text-gray-500">
                  <Handshake className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>I partner attivi appariranno qui dopo la conversione</p>
                  <p className="text-sm">Converti le richieste dalla tab "Richieste Partner"</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tenant Management Tab */}
          <TabsContent value="tenants">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-blue-600" />
                  Gestione Tenant
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Tenant</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Database</th>
                        <th className="text-left p-4 text-sm font-medium text-gray-600">Stato</th>
                        <th className="text-right p-4 text-sm font-medium text-gray-600">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map(tenant => (
                        <tr key={tenant.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <div className="font-medium">{tenant.nome || tenant.id}</div>
                          </td>
                          <td className="p-4">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {tenant.database_name || tenant.id}
                            </code>
                          </td>
                          <td className="p-4">
                            <StatusBadge status={tenant.status || 'attivo'} />
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => selectTenant(tenant)}
                                className="flex items-center gap-1"
                              >
                                <Eye className="h-4 w-4" />
                                Dettagli
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {tenants.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-500">
                            <Database className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p>Nessun tenant trovato</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Dettaglio Contatto</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedLead(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Nome</Label>
                  <p className="font-medium">{selectedLead.nome} {selectedLead.cognome}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Tipo</Label>
                  <p><Badge>{selectedLead.tipo === 'cliente' ? 'Ristorante' : 'Partner'}</Badge></p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <p>{selectedLead.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Telefono</Label>
                  <p>{selectedLead.telefono}</p>
                </div>
                {selectedLead.tipo === 'cliente' ? (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Ristorante</Label>
                      <p>{selectedLead.ristorante || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Città</Label>
                      <p>{selectedLead.citta || '-'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Azienda</Label>
                      <p>{selectedLead.azienda || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">P.IVA</Label>
                      <p>{selectedLead.partita_iva || '-'}</p>
                    </div>
                  </>
                )}
              </div>
              {selectedLead.messaggio && (
                <div>
                  <Label className="text-xs text-gray-500">Messaggio</Label>
                  <p className="p-3 bg-gray-50 rounded-lg text-sm">{selectedLead.messaggio}</p>
                </div>
              )}
              {selectedLead.esperienza && (
                <div>
                  <Label className="text-xs text-gray-500">Esperienza</Label>
                  <p className="p-3 bg-gray-50 rounded-lg text-sm">{selectedLead.esperienza}</p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedLead(null)}>Chiudi</Button>
                <Button onClick={() => window.location.href = `mailto:${selectedLead.email}`}>
                  <Mail className="h-4 w-4 mr-2" />Invia Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenant Detail Modal */}
      {showTenantModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="flex-shrink-0 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    {selectedTenant.nome || selectedTenant.id}
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    Database: <code className="bg-gray-100 px-2 py-0.5 rounded">{selectedTenant.database_name || selectedTenant.id}</code>
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={closeTenantModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-0">
              {loadingTenantData ? (
                <div className="p-8 text-center">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Caricamento dati...</p>
                </div>
              ) : (
                <Tabs defaultValue="stats" className="p-4">
                  <TabsList className="mb-4">
                    <TabsTrigger value="stats" className="flex items-center gap-1">
                      <BarChart3 className="h-4 w-4" />
                      Statistiche
                    </TabsTrigger>
                    <TabsTrigger value="logs" className="flex items-center gap-1">
                      <Activity className="h-4 w-4" />
                      Log ({tenantLogs.length})
                    </TabsTrigger>
                    <TabsTrigger value="users" className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Utenti ({tenantUsers.length})
                    </TabsTrigger>
                    <TabsTrigger value="backups" className="flex items-center gap-1">
                      <Archive className="h-4 w-4" />
                      Backup ({tenantBackups.length})
                    </TabsTrigger>
                  </TabsList>

                  {/* Stats Tab */}
                  <TabsContent value="stats">
                    <div className="space-y-4">
                      {tenantStats ? (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <Card>
                              <CardContent className="p-4">
                                <p className="text-2xl font-bold">{tenantStats.total_documents || 0}</p>
                                <p className="text-xs text-gray-500">Documenti totali</p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="p-4">
                                <p className="text-2xl font-bold">{Object.keys(tenantStats.collections || {}).length}</p>
                                <p className="text-xs text-gray-500">Collections</p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="p-4">
                                <p className="text-sm font-medium">{tenantStats.last_activity ? new Date(tenantStats.last_activity).toLocaleString('it-IT') : '-'}</p>
                                <p className="text-xs text-gray-500">Ultima attività</p>
                              </CardContent>
                            </Card>
                          </div>
                          {tenantStats.collections && Object.keys(tenantStats.collections).length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2">Collections</h4>
                              <div className="bg-gray-50 rounded-lg p-3 space-y-1 max-h-40 overflow-y-auto">
                                {Object.entries(tenantStats.collections).map(([name, count]) => (
                                  <div key={name} className="flex justify-between text-sm">
                                    <code>{name}</code>
                                    <span className="font-medium">{count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nessuna statistica disponibile</p>
                      )}

                      <div className="flex gap-2 pt-4 border-t">
                        <Button onClick={downloadTenantData} variant="outline" className="flex items-center gap-2">
                          <Download className="h-4 w-4" />
                          Scarica Dati
                        </Button>
                        <Button onClick={createTenantBackup} className="flex items-center gap-2">
                          <Archive className="h-4 w-4" />
                          Crea Backup
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Logs Tab */}
                  <TabsContent value="logs">
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {tenantLogs.length > 0 ? (
                        tenantLogs.map((log, idx) => (
                          <div key={log._id || idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <Badge className={
                                log.action_type?.includes('create') ? 'bg-green-100 text-green-700' :
                                log.action_type?.includes('delete') ? 'bg-red-100 text-red-700' :
                                log.action_type?.includes('update') ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {log.action_type || 'action'}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {log.timestamp ? new Date(log.timestamp).toLocaleString('it-IT') : '-'}
                              </span>
                            </div>
                            <p className="text-gray-600">{log.user_id || 'system'}</p>
                            {log.details && (
                              <pre className="text-xs bg-white mt-1 p-2 rounded overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nessun log disponibile</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Users Tab */}
                  <TabsContent value="users">
                    <div className="space-y-2">
                      {tenantUsers.length > 0 ? (
                        tenantUsers.map((user, idx) => (
                          <div key={user._id || idx} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{user.nome || user.email || user.username || 'Utente'}</p>
                              <p className="text-sm text-gray-500">{user.email || '-'}</p>
                            </div>
                            <Badge>{user.role || 'user'}</Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nessun utente trovato</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Backups Tab */}
                  <TabsContent value="backups">
                    <div className="space-y-2">
                      <div className="flex justify-end mb-4">
                        <Button onClick={createTenantBackup} size="sm" className="flex items-center gap-2">
                          <Archive className="h-4 w-4" />
                          Nuovo Backup
                        </Button>
                      </div>
                      {tenantBackups.length > 0 ? (
                        tenantBackups.map((backup, idx) => (
                          <div key={backup._id || idx} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <Badge className={
                                  backup.type === 'daily' ? 'bg-blue-100 text-blue-700' :
                                  backup.type === 'manual' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-700'
                                }>
                                  {backup.type || 'backup'}
                                </Badge>
                                <span className="text-sm text-gray-500 ml-2">
                                  {backup.created_at ? new Date(backup.created_at).toLocaleString('it-IT') : '-'}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => restoreTenantBackup(backup._id)}
                                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                >
                                  <History className="h-4 w-4 mr-1" />
                                  Ripristina
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600">
                              {backup.total_documents || 0} documenti • {backup.collections?.length || 0} collections
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nessun backup disponibile</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ControlPanelPage;
